import { LoadLimitFilterComponent, LoadLimitFilterOutput } from '../core/components/table/filter-panel/filters/load-limit-filter/load-limit-filter.component';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SortComponent } from '../core/components/sort/sort.component';
import { Router, RouterLink } from '@angular/router';
import { IsNewDirective } from '../../directives/is-new.directive';
import { Client, LimitType, LoadLimitDataOut, LoadLimitGetAllDataIn, LoadLimitStatus, MeterPhase, MeterTargetType, PageInfo, PermissionsEnum, SortingType } from '../../api/api-reference';
import { AuthService } from '../../services/auth.service';
import { getAssetTypeColor, getRegionColor } from '../events-alarms/alert-colors';
import { LoadLimitFilters } from '../core/models/selectedFilers';
import { SearchComponent } from '../core/components/search/search.component';
import { ToastrService } from 'ngx-toastr';
import { StatusComponent } from '../core/components/status/status.component';
import { ClipboardService } from '../../services/clipboard.service';
import { DateRangeInput } from '../core/models/date-range-input';
import { DatepickerComponent } from '../core/components/datepicker/datepicker.component';
import { PaginationComponent } from '../core/components/pagination/pagination.component';
import { processDateRange } from '../../helper/date-utils';
import { ConfirmDialogComponent } from '../core/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-load-limit',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    AngularSvgIconModule,
    SortComponent,
    RouterLink,
    PaginationComponent,
    IsNewDirective,
    LoadLimitFilterComponent,
    SearchComponent,
    StatusComponent,
    DatepickerComponent,
    ConfirmDialogComponent],
  templateUrl: './load-limit.component.html',
  styleUrl: './load-limit.component.scss'
})
export class LoadLimitComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfLoadLimits: number = 0;
  public lastPageSize: number = 1;
  activeRow: number | null = null;
  reminderStatus: LoadLimitStatus = new LoadLimitStatus();
  selectedRowId: string | null = null;

  selectedLimitTypes: string[] = [];
  selectedRegions: string[] = [];
  selectedAssetTypes: string[] = [];
  selectedFilters: LoadLimitFilters = {
    regions: [],
    assetTypes: [],
    limitTypes: []
  };
  filtersApplied = 0;
  
  isModalOpen = false;
  isDatepickerOpen = false;
  isActivateModalOpen = false;
  isDeactivateWarningModalOpen = false;
  private pendingActivateId: string | null = null;
  private pendingDeactivateId: string | null = null;

  readonly dayInMs = 24 * 60 * 60 * 1000;
  public PermissionEnum = PermissionsEnum;
  public LimitType = LimitType;

  loadLimitStatus: LoadLimitStatus = new LoadLimitStatus();
  targets = Object.values(MeterTargetType).map(s =>
    s.replace(/([A-Z])/g, ' $1').trim()
  );
  meterPhases = Object.values(MeterPhase).map(s =>
    s.replace(/([A-Z])/g, ' $1').trim()
  );

  getAssetTypeColor = getAssetTypeColor;
  getRegionColor = getRegionColor;

  requestData = new LoadLimitGetAllDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    sorting: SortingType.Descending,
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    assetTypes: [],
    limitTypes: [],
    regions: []
  });

  constructor(
    private client: Client,
    public authService: AuthService,
    private router: Router,
    public toastr: ToastrService,
    public clipboardService: ClipboardService,
  ) {
    this.reloadTable(1);
  }

  data: LoadLimitDataOut[] | undefined = [];

  onTargetOption(selectedTarget?: string) {
    if (selectedTarget !== undefined) {
      this.requestData.target =
        selectedTarget === 'ALL'
          ? undefined
          : selectedTarget.replace(/\s/g, '') as MeterTargetType;
    }
    this.reloadTable();
  }

  onMeterPhaseOption(selectedmeterPhase?: string) {
    if (selectedmeterPhase !== undefined) {
      this.requestData.meterPhase =
        selectedmeterPhase === 'ALL'
          ? undefined
          : selectedmeterPhase.replace(/\s/g, '') as MeterPhase;
    }
    this.reloadTable();
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  onDataReceived(event: LoadLimitFilterOutput) {
    this.selectedFilters = {
      assetTypes: [],
      limitTypes: event.selectedLimitTypes,
      regions: event.selectedRegions
    };
    this.requestData.regions = event.selectedRegions;
    this.requestData.limitTypes = event.selectedLimitTypes.map(
      (limitTypeStr: string) => {
        return LimitType[
          limitTypeStr as keyof typeof LimitType
        ]
      }
    );
    this.reloadTable();
  }
  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.selectedAssetTypes = [];
    this.selectedRegions = [];
    this.isModalOpen = false;

    this.client.queryLoadLimits(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfLoadLimits = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfLoadLimits / this.pageSize);
        this.filtersApplied =
          (this.requestData.assetTypes?.length || 0) +
          (this.requestData.regions?.length || 0) +
          (this.requestData.limitTypes?.length || 0);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    })
  }
  filterDatesInTable() {
    this.client.queryLoadLimits(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfLoadLimits = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfLoadLimits / this.pageSize);
        this.filtersApplied = this.requestData.assetTypes?.length || 0;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  editLoadLimit(id: string) {
    this.router.navigate(['load-limit-setup'], {
      state: { loadLimitData: id },
    });
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  setActiveRow(rowIndex: number | null) {
    this.activeRow = this.activeRow === rowIndex ? null : rowIndex;
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }
  openActivateModal(id: string) {
    this.pendingActivateId = id;
    this.isActivateModalOpen = true;
  }

  confirmActivate() {
    if (this.pendingActivateId) this.changeStatus(this.pendingActivateId, true);
    this.isActivateModalOpen = false;
    this.pendingActivateId = null;
  }

  cancelActivate() {
    this.isActivateModalOpen = false;
    this.pendingActivateId = null;
  }

  openDeactivateModal(row: LoadLimitDataOut) {
    this.pendingDeactivateId = row.id;
    if (row.assetTypeApplyLimiterConfiguration) {
      this.isDeactivateWarningModalOpen = true;
    } else {
      this.confirmDeactivate();
    }
  }

  confirmDeactivate() {
    if (this.pendingDeactivateId) this.changeStatus(this.pendingDeactivateId, false);
    this.isDeactivateWarningModalOpen = false;
    this.pendingDeactivateId = null;
  }

  cancelDeactivate() {
    this.isDeactivateWarningModalOpen = false;
    this.pendingDeactivateId = null;
  }

  changeStatus(id: string, isActive: boolean) {
    this.loadLimitStatus.loadLimitId = id;
    this.loadLimitStatus.isActive = isActive;

    this.client.loadLimitStatus(this.loadLimitStatus).subscribe({
      next: () => {
        this.toastr.success('Load limit successfuly changed status.');
        this.reloadTable();
        this.setActiveRow(null);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }
  delete(id: string) {
    this.client.deleteLoadLimit(id).subscribe({
      next: () => {
        this.toastr.success('Load limit successfuly deleted.');
        this.reloadTable();
        this.setActiveRow(null);
      },
      error: (err) => {
        this.toastr.error(err);
        this.isModalOpen = false;
      },
    })
  }
}